/**
 * F23 (Wave 2C) — admin commission-statements queue page.
 *
 * Route: `/commission-statements`
 * Permission: `payments:read` (super_admin + support + finance).
 *
 * Tabs filter: Draft / Issued / Settled / Voided / All (via `?status=`).
 * Default tab = Issued (the actionable queue — admin marks settled here).
 *
 * Row actions: View (link to detail) / Mark Settled (if status=issued) /
 * Void (if status ∈ {issued, settled}) / Manual issue (if status=draft).
 * Mark Settled + Void are gated server-side on `commission_statements:settle`;
 * the buttons are hidden on the queue list — admin clicks the row to
 * navigate to the detail page where the gated actions live.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useI18n } from '@bravemove/i18n/react';
import {
  Banner,
  Chip,
  CurrencyDisplay,
  EmptyState,
  InvoiceStatusBadge,
  type InvoiceSource,
  type InvoiceStatus,
  Pagination,
  Select,
  Skeleton,
  SourceTag,
  StatusChip,
  Table,
  TextInput,
  type TableColumn,
} from '@bravemove/ui-web';
import { COMMISSION_SOURCES } from '@bravemove/types';
import type { CommissionSource, CommissionStatementStatus } from '@bravemove/types';
import {
  adminCommissionApi,
  type AdminCommissionStatementRow,
  type ListStatementsOpts,
} from '../../api/commission';
import { translateApiError } from '../../auth/errors';
import { PageHeader } from '../../components/PageHeader';

const PAGE_SIZE = 25;

type UiFilter = CommissionStatementStatus | 'all';

const DEFAULT_FILTER: UiFilter = 'issued';

const FILTER_TABS: ReadonlyArray<{ value: UiFilter; labelKey: string; defaultLabel: string }> = [
  { value: 'draft', labelKey: 'admin:commission.tab.draft', defaultLabel: 'Draft' },
  { value: 'issued', labelKey: 'admin:commission.tab.issued', defaultLabel: 'Issued' },
  { value: 'settled', labelKey: 'admin:commission.tab.settled', defaultLabel: 'Settled' },
  { value: 'void', labelKey: 'admin:commission.tab.void', defaultLabel: 'Voided' },
  { value: 'all', labelKey: 'admin:commission.tab.all', defaultLabel: 'All' },
];

/**
 * Map the F23 statement status onto the `InvoiceStatusBadge` variants. `settled`
 * reads as `paid`; `void` has no badge variant and falls back to a `StatusChip`.
 */
function asInvoiceStatus(status: CommissionStatementStatus): InvoiceStatus | null {
  switch (status) {
    case 'draft':
      return 'draft';
    case 'issued':
      return 'issued';
    case 'settled':
      return 'paid';
    default:
      return null; // 'void' — no badge variant
  }
}

/**
 * Map the wider F23/F35/F38 `CommissionSource` onto the `SourceTag` variants.
 * `venue` (commission) maps to the primitive's `venue_commission`; trainer
 * sources have no `SourceTag` variant and fall through to a plain label.
 */
function asInvoiceSource(source: CommissionSource): InvoiceSource | null {
  switch (source) {
    case 'venue':
      return 'venue_commission';
    case 'venue_subscription':
      return 'venue_subscription';
    case 'venue_hybrid':
      return 'venue_hybrid';
    case 'venue_placement':
      return 'venue_placement';
    case 'tournament_listing':
      return 'tournament_listing';
    default:
      return null; // trainer_commission | trainer_flat — no SourceTag variant
  }
}

// F35 — the `source` filter is orthogonal to the status Chip-row (D15). It is a
// `<Select>` writing `?source=`. `'all'` = every source (no filter sent).
type UiSource = CommissionSource | 'all';

/**
 * CST-006 — exhaustive per-source default labels. Typed as a full
 * `Record<CommissionSource, string>` so adding a new source to the backend
 * enum FAILS compilation here instead of silently missing from the filter.
 */
const SOURCE_DEFAULT_LABELS: Record<CommissionSource, string> = {
  venue: 'Venue',
  trainer_commission: 'Trainer commission',
  trainer_flat: 'Trainer flat fee',
  venue_subscription: 'Venue subscription',
  venue_hybrid: 'Venue hybrid (fee + commission)',
  venue_placement: 'Placement',
  tournament_listing: 'Tournament listing',
};

// CST-006 — generated from the canonical COMMISSION_SOURCES enum so the
// Select can never drift from the backend's accepted values again (F39's
// venue_placement + F41's tournament_listing were missing from the
// hand-maintained list).
const SOURCE_OPTIONS: ReadonlyArray<{ value: UiSource; labelKey: string; defaultLabel: string }> = [
  { value: 'all', labelKey: 'commission:source.all', defaultLabel: 'All sources' },
  ...COMMISSION_SOURCES.map((s) => ({
    value: s,
    labelKey: `commission:source.${s}`,
    defaultLabel: SOURCE_DEFAULT_LABELS[s],
  })),
];

/** CST-013 — the backend requires `venueId: z.string().uuid()`. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function isUiFilter(value: string | null): value is UiFilter {
  return value !== null && FILTER_TABS.some((tab) => tab.value === value);
}

function isUiSource(value: string | null): value is UiSource {
  return value !== null && SOURCE_OPTIONS.some((o) => o.value === value);
}

function formatPeriod(periodStart: string, locale: string): string {
  try {
    const d = new Date(periodStart);
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      timeZone: 'Asia/Beirut',
    }).format(d);
  } catch {
    return periodStart;
  }
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '—';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function statusLabelKey(status: CommissionStatementStatus): {
  key: string;
  fallback: string;
} {
  switch (status) {
    case 'draft':
      return { key: 'commission:status.draft', fallback: 'Draft' };
    case 'issued':
      return { key: 'commission:status.issued', fallback: 'Issued' };
    case 'settled':
      return { key: 'commission:status.settled', fallback: 'Settled' };
    case 'void':
      return { key: 'commission:status.void', fallback: 'Voided' };
    default:
      return { key: 'commission:status.draft', fallback: status };
  }
}

export function CommissionStatementsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // CST-013 — validate the URL params instead of blind-casting: a hand-edited
  // `?status=foo` / `?source=bar` falls back to the defaults instead of being
  // sent to the backend's .strict() schema (which would 422-banner the page).
  const rawStatusParam = searchParams.get('status');
  const filterParam: UiFilter = isUiFilter(rawStatusParam) ? rawStatusParam : DEFAULT_FILTER;
  const rawSourceParam = searchParams.get('source');
  const sourceParam: UiSource = isUiSource(rawSourceParam) ? rawSourceParam : 'all';
  const pageParam = Number(searchParams.get('page') ?? '1') || 1;
  const venueIdFilter = searchParams.get('venueId') ?? '';
  // CST-013 — the backend requires a full UUID; while the admin types/pastes a
  // partial id every intermediate value would 422 and blank the table. Gate:
  // only a complete UUID is sent (the fetch deps key on the GATED value, so
  // intermediate keystrokes don't refetch at all).
  const venueIdParam = UUID_RE.test(venueIdFilter.trim()) ? venueIdFilter.trim() : '';

  const [rows, setRows] = useState<readonly AdminCommissionStatementRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const opts: ListStatementsOpts = {
        status: filterParam,
        page: pageParam,
        perPage: PAGE_SIZE,
        ...(venueIdParam ? { venueId: venueIdParam } : {}),
        ...(sourceParam !== 'all' ? { source: sourceParam } : {}),
      };
      const data = await adminCommissionApi.listStatements(opts);
      setRows(data.data);
      setTotal(data.pagination.total);
    } catch (err) {
      setError(translateApiError(err, t));
    } finally {
      setLoading(false);
    }
  }, [filterParam, sourceParam, pageParam, venueIdParam, t]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    void fetchList();
  }, [fetchList]);

  const onFilterChange = (value: UiFilter) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (value !== DEFAULT_FILTER) next.set('status', value);
      else next.delete('status');
      next.delete('page');
      return next;
    });
  };

  const onPageChange = (next: number) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      np.set('page', String(next));
      return np;
    });
  };

  const onVenueFilterChange = (value: string) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      if (value) np.set('venueId', value);
      else np.delete('venueId');
      np.delete('page');
      return np;
    });
  };

  const onSourceChange = (value: UiSource) => {
    setSearchParams((prev) => {
      const np = new URLSearchParams(prev);
      if (value !== 'all') np.set('source', value);
      else np.delete('source');
      np.delete('page');
      return np;
    });
  };

  const onClearFilters = () => {
    setSearchParams(new URLSearchParams());
  };

  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = useMemo<readonly TableColumn<AdminCommissionStatementRow>[]>(
    () => [
      {
        // F35 — polymorphic entity column. Venue-source rows render the venue
        // name + slug (F23 behaviour); trainer-source rows render the trainer's
        // display name (when the server returns one) or a "Trainer" label + the
        // trainerUserId until the server-side name lookup lands (2CD).
        key: 'entity',
        header: t('admin:commission.table.statements.column.venue', { defaultValue: 'Entity' }),
        cell: (row) => {
          const isTrainer = row.source === 'trainer_commission' || row.source === 'trainer_flat';
          if (isTrainer) {
            const label =
              row.trainerDisplayName ??
              t('commission:source.trainer_commission', { defaultValue: 'Trainer' });
            return (
              <div
                className="flex flex-col gap-0.5"
                data-testid={`commission-statements-row-trainer-${row.id}`}
              >
                {row.trainerUserId ? (
                  <a
                    className="text-[13px] text-ink font-semibold hover:underline"
                    href={`/trainers/${row.trainerUserId}/billing`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      navigate(`/trainers/${row.trainerUserId}/billing`);
                    }}
                    data-testid={`commission-statements-trainer-link-${row.id}`}
                  >
                    {label}
                  </a>
                ) : (
                  <span className="text-[13px] text-ink font-semibold">{label}</span>
                )}
                <span className="font-mono text-[11px] text-ink-3">{row.trainerUserId ?? '—'}</span>
              </div>
            );
          }
          return (
            <div
              className="flex flex-col gap-0.5"
              data-testid={`commission-statements-row-venue-${row.id}`}
            >
              <span className="text-[13px] text-ink font-semibold">{row.venueName}</span>
              <span className="font-mono text-[11px] text-ink-3">{row.venueSlug}</span>
            </div>
          );
        },
      },
      {
        key: 'source',
        header: t('admin:commission.table.statements.column.source', { defaultValue: 'Source' }),
        cell: (row) => {
          const src = asInvoiceSource(row.source);
          return src ? (
            <SourceTag
              source={src}
              label={t(`commission:source.${row.source}`, { defaultValue: row.source })}
            />
          ) : (
            <span className="font-mono text-[10px] uppercase tracking-[0.08em] text-ink-3">
              {t(`commission:source.${row.source}`, { defaultValue: row.source })}
            </span>
          );
        },
      },
      {
        key: 'period',
        header: t('admin:commission.table.statements.column.period', { defaultValue: 'Period' }),
        cell: (row) => (
          <span className="text-[13px] text-ink">{formatPeriod(row.periodStart, locale)}</span>
        ),
      },
      {
        key: 'status',
        header: t('admin:commission.table.statements.column.status', { defaultValue: 'Status' }),
        cell: (row) => {
          const lbl = statusLabelKey(row.status);
          const inv = asInvoiceStatus(row.status);
          return inv ? (
            <InvoiceStatusBadge
              size="sm"
              status={inv}
              label={t(lbl.key, { defaultValue: lbl.fallback })}
            />
          ) : (
            <StatusChip
              size="sm"
              status="rejected"
              label={t(lbl.key, { defaultValue: lbl.fallback })}
            />
          );
        },
      },
      {
        key: 'records',
        header: t('admin:commission.table.statements.column.records', {
          defaultValue: 'Records',
        }),
        align: 'end',
        cell: (row) => (
          <span className="font-mono text-[12.5px] text-ink-2 tabular-nums">
            {row.recordsCount}
          </span>
        ),
      },
      {
        key: 'gross',
        header: t('admin:commission.table.statements.column.gross', {
          defaultValue: 'Gross paid',
        }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.grossPaidAmountSum} currency="USD" locale={locale} />
        ),
      },
      {
        key: 'commission',
        header: t('admin:commission.table.statements.column.commission', {
          defaultValue: 'Commission',
        }),
        align: 'end',
        cell: (row) => (
          <CurrencyDisplay amount={row.commissionAmountSum} currency="USD" locale={locale} />
        ),
      },
      {
        key: 'issuedAt',
        header: t('admin:commission.table.statements.column.issued_at', {
          defaultValue: 'Issued',
        }),
        cell: (row) => (
          <span className="text-[12px] text-ink-2">{formatDate(row.issuedAt, locale)}</span>
        ),
      },
    ],
    [t, locale, navigate],
  );

  return (
    <div className="space-y-6" data-testid="commission-statements-page">
      <PageHeader
        title={t('admin:commission.page.statements.title', {
          defaultValue: 'Commission statements',
        })}
        subtitle={t('admin:commission.page.statements.description', {
          defaultValue:
            'Monthly per-venue invoices. Issued statements auto-fire at period close (1st of each month, Asia/Beirut).',
        })}
      />

      <div
        role="tablist"
        aria-label={t('admin:commission.page.statements.title', {
          defaultValue: 'Filter by status',
        })}
        className="flex flex-wrap items-center gap-2"
      >
        {FILTER_TABS.map((tab) => (
          <Chip
            key={tab.value}
            selected={filterParam === tab.value}
            onClick={() => onFilterChange(tab.value)}
            data-testid={`commission-statements-tab-${tab.value}`}
          >
            {t(tab.labelKey, { defaultValue: tab.defaultLabel })}
          </Chip>
        ))}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[200px] max-w-xs">
          <label
            className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1"
            htmlFor="commission-statements-source-filter"
          >
            {t('commission:source.filter_label', { defaultValue: 'Source' })}
          </label>
          <Select
            id="commission-statements-source-filter"
            value={sourceParam}
            options={SOURCE_OPTIONS.map((o) => ({
              value: o.value,
              label: t(o.labelKey, { defaultValue: o.defaultLabel }),
            }))}
            data-testid="commission-statements-source-filter"
            aria-label={t('commission:source.filter_label', { defaultValue: 'Source' })}
            onChange={(e) => onSourceChange(e.target.value as UiSource)}
          />
        </div>
        <div className="flex-1 min-w-[200px] max-w-xs">
          <label className="block text-[11px] uppercase tracking-wider text-ink-3 mb-1">
            {t('admin:commission.page.statements.filter.venue_label', { defaultValue: 'Venue' })}
          </label>
          <TextInput
            value={venueIdFilter}
            onChange={(e) => onVenueFilterChange(e.target.value)}
            placeholder={t('admin:commission.page.statements.filter.venue_placeholder', {
              defaultValue: 'Filter by venue id…',
            })}
            data-testid="commission-statements-venue-filter"
          />
        </div>
        {(venueIdFilter || sourceParam !== 'all' || filterParam !== DEFAULT_FILTER) && (
          <button
            type="button"
            className="text-[12px] text-accent hover:underline"
            onClick={onClearFilters}
            data-testid="commission-statements-clear-filters"
          >
            {t('admin:commission.page.statements.filter.clear', {
              defaultValue: 'Clear filters',
            })}
          </button>
        )}
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {loading ? (
        <div className="space-y-3" data-testid="commission-statements-loading">
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={t('admin:commission.page.statements.empty', {
            defaultValue: 'No commission statements in this view.',
          })}
        />
      ) : (
        <>
          <Table
            data={rows as AdminCommissionStatementRow[]}
            columns={columns as TableColumn<AdminCommissionStatementRow>[]}
            rowKey={(row) => row.id}
            onRowPress={(row) => navigate(`/commission-statements/${row.id}`)}
          />
          {pageCount > 1 ? (
            <div className="flex justify-end">
              <Pagination
                page={pageParam}
                pageCount={pageCount}
                onChange={onPageChange}
                ariaLabel={t('admin:commission.page.statements.title', {
                  defaultValue: 'Commission statements pagination',
                })}
              />
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

export default CommissionStatementsPage;
